import { ref, get, set, update } from 'firebase/database'
import { database } from '@/services/firebase'

export interface ControllerEntry {
  createdAt: number
  expiresAt: number
  isActive: boolean
  permissions: string[]
  controllerNumber?: number
}

/**
 * Count existing active controllers for a match
 * @param matchId - The match ID to count controllers for
 * @returns Promise<number> - Number of active controllers
 */
export const countExistingControllers = async (matchId: string): Promise<number> => {
  try {
    const controllersRef = ref(database, `matches/${matchId}/controllers`)
    const snapshot = await get(controllersRef)
    
    if (!snapshot.exists()) {
      return 0
    }
    
    const controllers = snapshot.val()
    const now = Date.now()
    
    // Count only active, non-expired controllers
    const activeControllers = Object.values(controllers).filter((controller: any) => {
      return controller.isActive && controller.expiresAt > now
    })
    
    return activeControllers.length
  } catch (error) {
    console.error('Error counting existing controllers:', error)
    return 0
  }
}

/**
 * Find the lowest available controller number for a match (fills gaps)
 * @param matchId - The match ID
 * @returns Promise<number> - The lowest available controller number (starting from 1)
 */
export const getNextControllerNumber = async (matchId: string): Promise<number> => {
  try {
    const controllersRef = ref(database, `matches/${matchId}/controllers`)
    const snapshot = await get(controllersRef)
    if (!snapshot.exists()) {
      return 1
    }
    const controllers = snapshot.val()
    // Collect all used numbers
    const usedNumbers = new Set<number>()
    Object.values(controllers).forEach((controller: any) => {
      if (controller.controllerNumber && typeof controller.controllerNumber === 'number') {
        usedNumbers.add(controller.controllerNumber)
      }
    })
    // Find the lowest unused number
    let n = 1
    while (usedNumbers.has(n)) {
      n++
    }
    return n
  } catch (error) {
    console.error('Error finding next controller number:', error)
    return 1
  }
}

/**
 * Generate a new controller token
 * @param matchId - The match ID to generate token for
 * @returns Promise<string> - The new controller token
 */
export const generateNewControllerToken = async (matchId: string): Promise<string> => {
  try {
    // Find the lowest available controller number
    const controllerNumber = await getNextControllerNumber(matchId)
    // Generate new token
    const newToken = crypto.randomUUID()
    // Create controller entry
    await createControllerEntry(matchId, newToken, controllerNumber)
    return newToken
  } catch (error) {
    throw new Error('Failed to generate controller token')
  }
}

/**
 * Create a controller entry in Firebase
 * @param matchId - The match ID
 * @param token - The controller token
 * @param controllerNumber - The sequential controller number
 * @returns Promise<void>
 */
export const createControllerEntry = async (
  matchId: string, 
  token: string, 
  controllerNumber: number
): Promise<void> => {
  try {
    const controllerRef = ref(database, `matches/${matchId}/controllers/${token}`)
    
    const controllerEntry: ControllerEntry = {
      createdAt: Date.now(),
      expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
      isActive: true,
      permissions: ['score', 'control'],
      controllerNumber
    }
    
    await set(controllerRef, controllerEntry)
  } catch (error) {
    throw new Error('Failed to create controller entry')
  }
}

/**
 * Validate if a controller token is valid and active
 * @param matchId - The match ID
 * @param token - The controller token to validate
 * @returns Promise<{isValid: boolean, controller?: ControllerEntry, error?: string}>
 */
export const validateControllerToken = async (
  matchId: string, 
  token: string
): Promise<{isValid: boolean, controller?: ControllerEntry, error?: string}> => {
  try {
    const controllerRef = ref(database, `matches/${matchId}/controllers/${token}`)
    const snapshot = await get(controllerRef)
    
    if (!snapshot.exists()) {
      return {
        isValid: false,
        error: 'Controller token does not exist'
      }
    }
    
    const controller = snapshot.val() as ControllerEntry
    const now = Date.now()
    
    // Check if token has expired
    if (controller.expiresAt < now) {
      return {
        isValid: false,
        error: 'Controller token has expired'
      }
    }
    
    // Check if controller is active
    if (!controller.isActive) {
      return {
        isValid: false,
        error: 'Controller token is inactive'
      }
    }
    
    return {
      isValid: true,
      controller
    }
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating controller token'
    }
  }
}

/**
 * Check if a token already exists and is being used
 * @param matchId - The match ID
 * @param token - The token to check
 * @returns Promise<boolean> - True if token exists and is active
 */
export const isTokenInUse = async (matchId: string, token: string): Promise<boolean> => {
  try {
    const validation = await validateControllerToken(matchId, token)
    return validation.isValid
  } catch (error) {
    return false
  }
}

/**
 * Clean up expired controllers for a match
 * @param matchId - The match ID
 * @param cutoffMinutes - Minutes after which controllers are considered expired (default: 20)
 * @returns Promise<number> - Number of controllers removed
 */
export const cleanupExpiredControllers = async (
  matchId: string, 
  cutoffMinutes: number = 20
): Promise<number> => {
  try {
    const controllersRef = ref(database, `matches/${matchId}/controllers`)
    const snapshot = await get(controllersRef)
    
    if (!snapshot.exists()) {
      return 0
    }
    
    const controllers = snapshot.val()
    const cutoffTime = Date.now() - (cutoffMinutes * 60 * 1000)
    const controllersToRemove: string[] = []
    
    // Find controllers older than cutoff time
    Object.keys(controllers).forEach(token => {
      const controller = controllers[token]
      // Always preserve controller #1 (the main display/controller)
      if (controller.createdAt < cutoffTime && controller.controllerNumber !== 1) {
        controllersToRemove.push(token)
      }
    })
    
    // Remove old controllers
    if (controllersToRemove.length > 0) {
      const updates: any = {}
      controllersToRemove.forEach(token => {
        updates[`matches/${matchId}/controllers/${token}`] = null
      })
      
      await update(ref(database), updates)
      return controllersToRemove.length
    }
    
    return 0
  } catch (error) {
    return 0
  }
}

/**
 * Get all active controllers for a match
 * @param matchId - The match ID
 * @returns Promise<{[token: string]: ControllerEntry}>
 */
export const getActiveControllers = async (
  matchId: string
): Promise<{[token: string]: ControllerEntry}> => {
  try {
    const controllersRef = ref(database, `matches/${matchId}/controllers`)
    const snapshot = await get(controllersRef)
    
    if (!snapshot.exists()) {
      return {}
    }
    
    const controllers = snapshot.val()
    const now = Date.now()
    
    // Filter for active, non-expired controllers
    const activeControllers: {[token: string]: ControllerEntry} = {}
    
    Object.keys(controllers).forEach(token => {
      const controller = controllers[token]
      if (controller.isActive && controller.expiresAt > now) {
        activeControllers[token] = controller
      }
    })
    
    return activeControllers
  } catch (error) {
    return {}
  }
} 